import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { checkIsAdmin } from '@/data/producers';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

type CallbackState = 'loading' | 'no-session';

export default function AdminCallback() {
  const navigate = useNavigate();
  const [state, setState] = useState<CallbackState>('loading');

  useEffect(() => {
    const handleCallback = async () => {
      // Wait for auth to process the OAuth callback and restore session.
      let session = null as (Awaited<ReturnType<typeof supabase.auth.getSession>>['data']['session']);
      let error: any = null;

      for (let attempt = 0; attempt < 5; attempt++) {
        const result = await supabase.auth.getSession();
        session = result.data.session;
        error = result.error;
        if (session || error) break;
        await new Promise((r) => setTimeout(r, 200));
      }

      if (error || !session) {
        setState('no-session');
        return;
      }

      // Check if the user is an admin
      const isAdmin = await checkIsAdmin();

      if (!isAdmin) {
        await supabase.auth.signOut();
        navigate('/admin/login?error=not_admin', { replace: true });
        return;
      }

      navigate('/admin/producers', { replace: true });
    };

    handleCallback();
  }, [navigate]);

  if (state === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto mb-4" />
          <p className="text-sm text-muted-foreground">Slutför inloggning...</p>
        </div>
      </div>
    );
  }

  if (state === 'no-session') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="w-full max-w-sm">
          <CardHeader className="text-center">
            <AlertCircle className="h-10 w-10 text-destructive mx-auto mb-2" />
            <CardTitle className="text-lg">Inloggning misslyckades</CardTitle>
            <CardDescription>
              Kunde inte logga in. Försök igen.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <Link to="/admin/login">Tillbaka till inloggning</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Should never reach here, but keep a safe fallback.
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}
